import { FC, useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import { AlertTriangle, Building, ChevronDown, ChevronUp, Plus, Trash2 } from "lucide-react";
import {
  BuildingCommand,
  BuildingFuture,
  BuildingOccupancy,
  CopropertyDto,
  CopropertyEligibilityStatus,
  UpdateCopropertyBuildingsCommand,
  UpdateCopropertyResponse,
} from "api/coproperty";
import { Dropdown, DropdownItem } from "components/forms/Dropdown";
import { InputText } from "components/forms/InputText";
import { InputWithUnit } from "components/forms/fragments/InputWithUnit";
import { Button } from "components/ui/Button";
import {
  BooleanChoice,
  EligibilityVerdict,
  EligibilityVerdictBanner,
  FUTURE_OPTIONS,
  OCCUPANCY_OPTIONS,
  parseDecimal,
  toVerdict,
} from "./CopropertyFieldControls";
import { CopropertySection } from "./CopropertyStepLayout";

interface CopropertyBuildingsFormProps {
  coproperty: CopropertyDto;
  /** Enregistre la liste des bâtiments ; la réponse porte le verdict de la sous-étape. */
  onSave: (command: UpdateCopropertyBuildingsCommand) => Promise<UpdateCopropertyResponse | null>;
  /** Enregistré et éligible : la sous-étape est complète. */
  onEligibleSaved: () => void;
  /** Enregistré mais refusé (niveaux, bâtiment vide non muré) : sortie de souscription. */
  onRefused: (reasons: string[]) => void;
  /** Expose l'enregistrement au bouton d'étape de la page. */
  registerSubmit: (submit: () => Promise<void>) => void;
  /**
   * Remonte à la page si la saisie est enregistrable : chaque bâtiment complet et la somme des
   * surfaces dans la copropriété. Le bouton d'étape reste désactivé tant que ce n'est pas le cas.
   */
  onReadyChange: (isReady: boolean) => void;
}

type BuildingDraft = BuildingCommand & { key: string };

/** Niveaux au-dessus du sol proposés : R+0 à R+7 ; au-delà, la copropriété est refusée. */
const MAX_FLOORS = 7;
const ABOVE_MAX_FLOORS = MAX_FLOORS + 1;
const FLOORS_OPTIONS = [
  ...Array.from({ length: MAX_FLOORS + 1 }, (_, n) => ({ value: n, label: n === 0 ? "Rez-de-chaussée seul" : `R+${n}` })),
  { value: ABOVE_MAX_FLOORS, label: `Plus de R+${MAX_FLOORS}` },
];

/** Options des listes déroulantes génériques (niveaux, devenir). */
const FLOORS_ITEMS: DropdownItem<number>[] = FLOORS_OPTIONS.map((o) => ({ key: o.value, label: o.label }));
const FUTURE_ITEMS: DropdownItem<number>[] = FUTURE_OPTIONS.map((o) => ({ key: o.value, label: o.label }));
const itemFor = (items: DropdownItem<number>[], value: number | null | undefined) =>
  value == null ? null : (items.find((item) => item.key === value) ?? null);

let draftKeySeed = 0;
const nextDraftKey = () => `batiment-${++draftKeySeed}`;

const newDraft = (): BuildingDraft => ({
  key: nextDraftKey(),
  floorsCount: null,
  basementsCount: null,
  area: null,
  occupancy: null,
  accessWalledUp: null,
  future: null,
});

const isDraftComplete = (b: BuildingDraft) =>
  b.area != null && b.floorsCount != null && b.occupancy != null;

/**
 * Sorties de souscription connues côté serveur, annoncées AVANT l'enregistrement pour
 * que le courtier corrige (ou retire le bâtiment) en connaissance de cause. Le verdict
 * qui fait foi reste celui du serveur, rendu après l'enregistrement.
 */
const draftBlockingReason = (b: BuildingDraft): string | null => {
  if (b.floorsCount != null && b.floorsCount > MAX_FLOORS) {
    return `Un bâtiment de plus de R+${MAX_FLOORS} ne peut pas être assuré.`;
  }
  if (b.occupancy === 2 && b.accessWalledUp === false) {
    return "Un bâtiment totalement vide doit avoir ses accès murés pour être assuré.";
  }
  return null;
};

/** Résumé d'une carte repliée ("1 850 m² · R+4 · 2 sous-sols · Plus de 50 %"). */
const draftSummary = (b: BuildingDraft): string => {
  const parts = [
    b.area != null && `${b.area.toLocaleString("fr-FR")} m²`,
    b.floorsCount != null &&
      (b.floorsCount > MAX_FLOORS ? `Plus de R+${MAX_FLOORS}` : b.floorsCount === 0 ? "Rez-de-chaussée seul" : `R+${b.floorsCount}`),
    b.basementsCount != null && `${b.basementsCount} sous-sol${b.basementsCount > 1 ? "s" : ""}`,
    OCCUPANCY_OPTIONS.find((o) => o.value === b.occupancy)?.label,
  ].filter((p): p is string => Boolean(p));

  return parts.length > 0 ? parts.join(" · ") : "À décrire";
};

const StatusBadge: FC<{ draft: BuildingDraft }> = ({ draft }) => {
  if (draftBlockingReason(draft)) {
    return (
      <span className="inline-flex flex-none items-center gap-1.5 rounded-full border border-red-200 bg-red-50 px-2.5 py-0.5 text-xs font-semibold text-red-700">
        <AlertTriangle className="h-3 w-3" />
        Bloquant
      </span>
    );
  }
  if (isDraftComplete(draft)) {
    return (
      <span className="inline-flex flex-none items-center gap-1.5 rounded-full border border-green-200 bg-green-50 px-2.5 py-0.5 text-xs font-semibold text-green-700">
        Complet
      </span>
    );
  }
  return (
    <span className="inline-flex flex-none items-center rounded-full border border-amber-200 bg-amber-50 px-2.5 py-0.5 text-xs font-semibold text-amber-700">
      Incomplet
    </span>
  );
};

/**
 * Boucle des bâtiments de la copropriété (sous-étape COPROPERTY_BUILDINGS du dossier
 * MRI), en cartes empilées dans la section « Bâtiments » de la carte principale : résumé
 * replié, édition dépliée. Les sorties (plus de R+7, bâtiment totalement vide non muré)
 * sont pré-annoncées à la saisie puis confirmées côté serveur après l'enregistrement,
 * déclenché par le bouton d'étape de la page.
 */
export const CopropertyBuildingsForm: FC<CopropertyBuildingsFormProps> = ({
  coproperty,
  onSave,
  onEligibleSaved,
  onRefused,
  registerSubmit,
  onReadyChange,
}) => {
  const [buildings, setBuildings] = useState<BuildingDraft[]>(() => {
    const existing =
      coproperty.buildings?.map((b) => ({
        key: nextDraftKey(),
        id: b.id,
        floorsCount: b.floorsCount ?? null,
        basementsCount: b.basementsCount ?? null,
        area: b.area ?? null,
        occupancy: b.occupancy ?? null,
        accessWalledUp: b.accessWalledUp ?? null,
        future: b.future ?? null,
      })) ?? [];
    return existing.length > 0 ? existing : [newDraft()];
  });
  const [expandedKey, setExpandedKey] = useState<string | null>(() => buildings[0]?.key ?? null);
  const [isSaving, setIsSaving] = useState(false);
  const [verdict, setVerdict] = useState<EligibilityVerdict | null>(null);

  const updateBuilding = (key: string, patch: Partial<BuildingCommand>) => {
    setVerdict(null);
    setBuildings((current) => current.map((b) => (b.key === key ? { ...b, ...patch } : b)));
  };

  const addBuilding = () => {
    const draft = newDraft();
    setBuildings((current) => [...current, draft]);
    setExpandedKey(draft.key);
  };

  const removeBuilding = (key: string) => {
    setVerdict(null);
    setBuildings((current) => {
      const remaining = current.filter((b) => b.key !== key);
      const next = remaining.length > 0 ? remaining : [newDraft()];
      if (key === expandedKey) setExpandedKey(next[0].key);
      return next;
    });
  };

  const handleSave = useCallback(async () => {
    if (!coproperty.id) return;

    const incomplete = buildings.findIndex((b) => !isDraftComplete(b));
    if (incomplete >= 0) {
      setExpandedKey(buildings[incomplete].key);
      toast.warn(`Le bâtiment ${incomplete + 1} doit avoir une surface, un nombre de niveaux et une occupation.`);
      return;
    }

    // Règle métier annoncée avant l'aller-retour : les bâtiments tiennent dans la copropriété
    // (somme des surfaces inférieure ou égale). Le serveur la vérifie aussi, dans le verdict.
    const buildingsArea = buildings.reduce((sum, b) => sum + (b.area ?? 0), 0);
    if (coproperty.area != null && buildingsArea > coproperty.area) {
      toast.warn(
        `La somme des surfaces des bâtiments (${buildingsArea.toLocaleString("fr-FR")} m²) dépasse la surface de la copropriété (${coproperty.area.toLocaleString("fr-FR")} m²).`
      );
      return;
    }

    setIsSaving(true);
    try {
      const command: UpdateCopropertyBuildingsCommand = {
        buildings: buildings.map(({ key: _key, ...building }) => ({
          ...building,
          // « Plus de R+7 » est déclaré au-delà du seuil : le serveur prononce le refus.
          floorsCount: building.floorsCount === ABOVE_MAX_FLOORS ? MAX_FLOORS + 1 : building.floorsCount,
          accessWalledUp: building.occupancy === 2 ? building.accessWalledUp : null,
        })),
      };

      const response = await onSave(command);
      if (!response) return;

      // Le verdict de la sous-étape arrive avec l'enregistrement : pas de second appel.
      const evaluation = toVerdict(response.eligibility);
      setVerdict(evaluation);

      if (!evaluation) {
        toast.error("La vérification d'éligibilité a échoué — réessayez.");
        return;
      }

      if (evaluation.status === CopropertyEligibilityStatus.NonEligible) {
        onRefused(evaluation.reasons);
        return;
      }

      if (evaluation.status === CopropertyEligibilityStatus.ACompleter) {
        toast.warn("Bâtiments enregistrés, mais des informations manquent.");
        return;
      }

      toast.success("Bâtiments enregistrés.");
      onEligibleSaved();
    } catch (error) {
      console.error(error);
      toast.error("L'enregistrement des bâtiments a échoué.");
    } finally {
      setIsSaving(false);
    }
  }, [coproperty.id, coproperty.area, buildings, onSave, onRefused, onEligibleSaved]);

  useEffect(() => {
    registerSubmit(handleSave);
  }, [registerSubmit, handleSave]);

  const isReady = useMemo(() => {
    if (!buildings.every(isDraftComplete)) return false;
    const buildingsArea = buildings.reduce((sum, b) => sum + (b.area ?? 0), 0);
    return coproperty.area == null || buildingsArea <= coproperty.area;
  }, [buildings, coproperty.area]);

  useEffect(() => {
    onReadyChange(isReady);
  }, [onReadyChange, isReady]);

  return (
    <CopropertySection
      title="Bâtiments"
      titleAside={
        <Button color="secondary" round disabled={isSaving} onClick={addBuilding}>
          <Plus size={16} />
          Ajouter un bâtiment
        </Button>
      }
    >
      {buildings.map((building, index) => {
        const isExpanded = building.key === expandedKey;
        const blockingReason = draftBlockingReason(building);

        return (
          <section
            key={building.key}
            className={`rounded-2xl border bg-white ${
              isExpanded ? "border-primary ring-[3px] ring-primary/10" : "border-gray-200"
            }`}
          >
            {/* En-tête de carte : résumé + statut, cliquable pour déplier/replier */}
            <button
              type="button"
              onClick={() => setExpandedKey(isExpanded ? null : building.key)}
              className="flex w-full items-center gap-4 px-5 py-4 text-left"
            >
              <Building className={`h-5 w-5 flex-none ${isExpanded ? "text-primary" : "text-gray-500"}`} />
              <div className="min-w-0 flex-1">
                <p className="text-[15px] font-semibold text-zinc-800">Bâtiment {index + 1}</p>
                {!isExpanded && <p className="truncate text-[13px] text-gray-500">{draftSummary(building)}</p>}
              </div>
              <StatusBadge draft={building} />
              {isExpanded ? (
                <ChevronUp className="h-4 w-4 flex-none text-gray-500" />
              ) : (
                <ChevronDown className="h-4 w-4 flex-none text-gray-500" />
              )}
            </button>

            {isExpanded && (
              <div className="flex flex-col gap-4 border-t border-gray-100 px-5 pb-5 pt-4">
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                  <InputWithUnit
                    label="Surface du bâtiment"
                    name={`${building.key}-area`}
                    unit="m²"
                    type="number"
                    min={0}
                    value={building.area != null ? String(building.area) : ""}
                    onChange={(e) => updateBuilding(building.key, { area: parseDecimal(e.target.value) })}
                  />
                  <Dropdown
                    label="Niveaux au-dessus du sol"
                    name={`${building.key}-floors`}
                    placeholder="Sélectionner"
                    items={FLOORS_ITEMS}
                    selectedItem={itemFor(FLOORS_ITEMS, building.floorsCount)}
                    onSelect={(item) => updateBuilding(building.key, { floorsCount: Number(item.key) })}
                  />
                  <InputText
                    label="Nombre de sous-sols"
                    type="number"
                    min={0}
                    value={building.basementsCount != null ? String(building.basementsCount) : ""}
                    onChange={(e) =>
                      updateBuilding(building.key, {
                        basementsCount: e.target.value ? Number(e.target.value) : null,
                      })
                    }
                  />
                </div>

                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <div>
                    <span className="mb-1 block text-sm font-medium text-gray-700">Occupation du bâtiment</span>
                    <div className="flex flex-wrap gap-2">
                      {OCCUPANCY_OPTIONS.map((o) => (
                        <button
                          key={o.value}
                          type="button"
                          onClick={() => updateBuilding(building.key, { occupancy: o.value as BuildingOccupancy })}
                          className={`rounded-lg border px-4 py-2 text-sm font-medium transition-colors ${
                            building.occupancy === o.value
                              ? "border-primary bg-primary/10 text-primary"
                              : "border-gray-300 text-gray-600 hover:bg-gray-50"
                          }`}
                        >
                          {o.label}
                        </button>
                      ))}
                    </div>
                  </div>
                  <Dropdown
                    label="Réhabilitation / démolition prévue"
                    name={`${building.key}-future`}
                    placeholder="Sélectionner"
                    items={FUTURE_ITEMS}
                    selectedItem={itemFor(FUTURE_ITEMS, building.future)}
                    onSelect={(item) => updateBuilding(building.key, { future: Number(item.key) as BuildingFuture })}
                  />
                </div>

                {building.occupancy === 2 && (
                  <BooleanChoice
                    label="Des murs bloquent-ils les accès de ce bâtiment vide ?"
                    value={building.accessWalledUp}
                    hint="Un bâtiment totalement vide doit avoir ses accès murés pour être assuré."
                    onChange={(value) => updateBuilding(building.key, { accessWalledUp: value })}
                  />
                )}

                {blockingReason && (
                  <div className="flex gap-2.5 rounded-xl border border-red-200 bg-red-50 p-3.5">
                    <AlertTriangle className="mt-0.5 h-4 w-4 flex-none text-red-700" />
                    <div className="text-[13px] text-red-700">
                      <p className="font-bold">Ce bâtiment rendra la copropriété non assurable</p>
                      <p>{blockingReason} Corrigez la saisie ou retirez le bâtiment avant d'enregistrer.</p>
                    </div>
                  </div>
                )}

                <div className="flex justify-end">
                  <button
                    type="button"
                    onClick={() => removeBuilding(building.key)}
                    className="inline-flex items-center gap-1.5 text-sm text-gray-400 hover:text-red-600"
                  >
                    <Trash2 className="h-4 w-4" />
                    Supprimer ce bâtiment
                  </button>
                </div>
              </div>
            )}
          </section>
        );
      })}

      {verdict && <EligibilityVerdictBanner verdict={verdict} />}
    </CopropertySection>
  );
};
